import React, { useMemo } from "react";
import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  FolderOpen,
  CheckCircle2,
  FileEdit,
  Settings,
  TrendingUp,
  TrendingDown,
  Minus,
  Clock,
} from "lucide-react";

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.35,
      ease: [0.4, 0, 0.2, 1],
    },
  }),
};

function StatCard({ title, value, subtitle, icon: Icon, color, trend, index }) {
  return (
    <motion.div
      custom={index}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover={{ y: -2, transition: { duration: 0.15 } }}
      className={`admin-stat-card admin-stat-card--${color}`}
    >
      <div className="admin-stat-card-top">
        <div className={`admin-stat-icon admin-stat-icon--${color}`}>
          <Icon size={20} strokeWidth={2} />
        </div>
        <span
          className={`admin-stat-trend ${
            trend > 0
              ? "admin-stat-trend--up"
              : trend < 0
              ? "admin-stat-trend--down"
              : "admin-stat-trend--flat"
          }`}
        >
          {trend > 0 ? (
            <TrendingUp size={13} />
          ) : trend < 0 ? (
            <TrendingDown size={13} />
          ) : (
            <Minus size={13} />
          )}
          {trend !== 0 ? `${Math.abs(trend)}%` : "—"}
        </span>
      </div>
      <p className="admin-stat-value">{value}</p>
      <p className="admin-stat-title">{title}</p>
      <p className="admin-stat-subtitle">{subtitle}</p>
    </motion.div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="admin-chart-tooltip">
        <p className="admin-chart-tooltip-label">{label}</p>
        <p className="admin-chart-tooltip-value">{payload[0].value} projects</p>
      </div>
    );
  }
  return null;
};

export default function AdminOverview({ projects = [] }) {
  const total = projects.length;
  const published = projects.filter(
    (p) => p.status === "published" || (!p.status && !p.draft)
  ).length;
  const drafts = projects.filter((p) => p.status === "draft" || p.draft).length;

  // Build monthly chart data from projects
  const chartData = useMemo(() => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const counts = new Array(12).fill(0);
    projects.forEach((p) => {
      const d = new Date(p.createdAt || p.date || Date.now());
      if (!isNaN(d)) counts[d.getMonth()]++;
    });
    return months.map((m, i) => ({ month: m, projects: counts[i] }));
  }, [projects]);

  // Recent 5 projects
  const recent = useMemo(
    () =>
      [...projects]
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .slice(0, 5),
    [projects]
  );

  const stats = [
    {
      title: "Total Projects",
      value: total,
      subtitle: "All time",
      icon: FolderOpen,
      color: "green",
      trend: 12,
    },
    {
      title: "Published",
      value: published,
      subtitle: "Live & visible",
      icon: CheckCircle2,
      color: "blue",
      trend: 8,
    },
    {
      title: "Drafts",
      value: drafts,
      subtitle: "Unpublished",
      icon: FileEdit,
      color: "amber",
      trend: -3,
    },
    {
      title: "Modules",
      value: 3,
      subtitle: "Active study modules",
      icon: Settings,
      color: "purple",
      trend: 0,
    },
  ];

  return (
    <div className="admin-overview">
      {/* Page Title */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="admin-overview-header"
      >
        <div>
          <h1 className="admin-overview-title">Dashboard Overview</h1>
          <p className="admin-overview-subtitle">
            Welcome back — here's what's happening.
          </p>
        </div>
        <div className="admin-overview-date">
          <Clock size={14} />
          <span>
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </span>
        </div>
      </motion.div>

      {/* KPI Stat Cards */}
      <div className="admin-stat-grid">
        {stats.map((s, i) => (
          <StatCard key={s.title} {...s} index={i} />
        ))}
      </div>

      {/* Chart + Recent Activity */}
      <div className="admin-overview-bottom">
        {/* Bar Chart */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.4 }}
          className="admin-chart-card"
        >
          <div className="admin-card-header">
            <h2 className="admin-card-title">Projects by Month</h2>
            <span className="admin-card-badge">This Year</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: "var(--admin-text-muted)" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "var(--admin-text-muted)" }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "var(--admin-sidebar-hover)" }} />
              <Bar
                dataKey="projects"
                fill="var(--admin-primary)"
                radius={[4, 4, 0, 0]}
                maxBarSize={32}
              />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Recent Projects */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.4 }}
          className="admin-recent-card"
        >
          <div className="admin-card-header">
            <h2 className="admin-card-title">Recent Projects</h2>
            <span className="admin-card-badge">Latest 5</span>
          </div>
          {recent.length === 0 ? (
            <div className="admin-empty-state">
              <FolderOpen size={32} className="admin-empty-icon" />
              <p>No projects yet.</p>
            </div>
          ) : (
            <div className="admin-recent-list">
              {recent.map((p, i) => (
                <motion.div
                  key={p._id}
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + i * 0.06, duration: 0.25 }}
                  className="admin-recent-item"
                >
                  <div className="admin-recent-index">{i + 1}</div>
                  <div className="admin-recent-info">
                    <p className="admin-recent-name">{p.title || "Untitled"}</p>
                    <p className="admin-recent-date">
                      {p.createdAt
                        ? new Date(p.createdAt).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        : "—"}
                    </p>
                  </div>
                  <span
                    className={`admin-status-badge ${
                      p.draft
                        ? "admin-status-badge--draft"
                        : "admin-status-badge--live"
                    }`}
                  >
                    {p.draft ? "Draft" : "Live"}
                  </span>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
